<template>
  <div>
    <div class="d-flex justify-content-end mb-2">
      <b-button variant="outline-secondary" size="sm" class="mr-1" :disabled="importing" @click="showImport = !showImport">
        {{ $t(showImport ? 'unit.add_from_form' : 'unit.contacts_import_template') }}
      </b-button>
      <b-button variant="outline-primary" size="sm" :disabled="downloadingTemplate" @click="downloadContactsTemplate">
        <b-spinner v-if="downloadingTemplate" small />
        {{ $t('unit.download_template') }}
      </b-button>
    </div>
    <b-alert :show="!!templateDownloadError" variant="danger">
      {{ templateDownloadError ? $t(`unit.${templateDownloadError}`) : '' }}
    </b-alert>
    <div v-if="showImport" class="mb-2">
      <p>{{ $t('unit.contacts_template_hint') }}</p>
      <b-form-group :label="$t('unit.template')" label-for="contacts-template">
        <b-form-file id="contacts-template" v-model="templateFile" accept=".xlsx" :disabled="importing"
          :browse-text="$t('unit.search')" :placeholder="$t('unit.select_template')" />
      </b-form-group>
      <b-button variant="primary" :disabled="!templateFile || importing" @click="importContacts">
        <b-spinner v-if="importing" small />
        {{ $t('actions.accept') }}
      </b-button>
      <b-alert :show="!!importError" variant="danger" class="mt-1">{{ importError }}</b-alert>
    </div>
    <validation-observer ref="refFormObserver">
      <b-form>
        <!-- Lista de contactos -->
        <div v-for="(contact, index) in itemData.emergency_contact" :key="index">
          <b-row class="mb-2">
            <b-col cols="2">
              <validation-provider
                #default="validationContext"
                 :vid="`section_${index}`"
                :name="$t('unit.section')"
                rules="required"
              >
                <b-form-group
                  :label="$t('unit.section')"
                  :state="getValidationState(validationContext)"
                >
                  <v-select
                    v-model="contact.section"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="teamsOptions"
                    :reduce="(val) => val.label"
                    :input-id="`section_${index}`"
                  />
                  <b-form-invalid-feedback :state="getValidationState(validationContext)">
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>
            <b-col cols="2">
              <validation-provider
                #default="validationContext"
                :vid="`phone_number_${index}`"
                :name="$t('unit.phone_number')"
                rules="required"
              >
                <b-form-group
                  :label="$t('unit.phone_number')"
                  :state="getValidationState(validationContext)"
                >
                  <b-form-input
                    v-model="contact.phone_number"
                    :state="getValidationState(validationContext)"
                    trim
                  />
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>
            <b-col cols="2">
              <validation-provider
                #default="validationContext"
                :vid="`name_${index}`"
                :name="$t('unit.name')"
                rules="required"
              >
                <b-form-group
                  :label="$t('unit.name')"
                  :state="getValidationState(validationContext)"
                >
                  <b-form-input
                    v-model="contact.name"
                    :state="getValidationState(validationContext)"
                    trim
                  />
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>
            <b-col cols="4">
              <validation-provider
                #default="validationContext"
                :vid="`description_${index}`"
                :name="$t('unit.description')"
                rules="required"
              >
                <b-form-group
                  :label="$t('unit.description')"
                  :state="getValidationState(validationContext)"
                >
                  <b-form-input
                    v-model="contact.description"
                    :state="getValidationState(validationContext)"
                    trim
                  />
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>
            <b-col cols="auto">
              <div class="form-group mb-0">
                <label class="d-block">&nbsp;</label>
                <b-button variant="danger" @click="removeContact(index)" class="mr-2">
                  {{ $t('unit.remove_contact') }}
                </b-button>
              </div>
            </b-col>
          </b-row>
        </div>

        <!-- Botón para añadir un nuevo contacto -->
        <b-button variant="secondary" @click="addContact">{{ $t('unit.add_contact') }}</b-button>
      </b-form>
    </validation-observer>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BForm,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BFormFile,
  BAlert,
  BSpinner,
} from "bootstrap-vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import formValidation from "@/@core/comp-functions/forms/form-validation";
import { ref, computed, watch, onBeforeUnmount } from "@vue/composition-api";
import readContactsTemplate from "./readContactsTemplate";
import useContactsTemplateDownload from "./useContactsTemplateDownload";
import vSelect from "vue-select";
import store from "@/store";

export default {
  name: "EmergencyContacts",
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BFormFile,
    BAlert,
    BSpinner,
    ValidationProvider,
    ValidationObserver,
    vSelect,
  },
  props: {
    isAdd: {
      type: Boolean,
      default: false,
    },
    propItemData: {
      type: Object,
      default: () => ({}),
    },
  },
  setup(props, { emit, root }) {
    const templateDownload = useContactsTemplateDownload();
    const { refFormObserver, getValidationState } = formValidation();

    // Section is picked from the teams catalog, but only its name is stored
    // (see the `reduce` on the v-select), so free-text sections saved before
    // this change keep rendering as-is.
    store.dispatch("teams/getAll");
    const teamsOptions = computed(() => store.getters["teams/getSelectedItems"]);

    const itemData = ref({
      ...props.propItemData,
      emergency_contact: props.propItemData.emergency_contact || []
    });

    const showImport = ref(false);
    const templateFile = ref(null);
    const importing = ref(false);
    const importError = ref("");
    let disposed = false;
    onBeforeUnmount(() => { disposed = true; });
    watch(templateFile, () => { importError.value = ""; });

    const importContacts = async () => {
      if (!templateFile.value || importing.value) return;
      importing.value = true;
      importError.value = "";
      try {
        const contacts = await readContactsTemplate(templateFile.value);
        if (disposed) return;
        itemData.value.emergency_contact = [...itemData.value.emergency_contact, ...contacts];
        emit("update:itemData", itemData.value);
        templateFile.value = null;
        showImport.value = false;
      } catch (error) {
        if (disposed) return;
        const key = ['contacts_template_invalid', 'contacts_template_browser', 'contacts_template_empty'].includes(error.message)
          ? error.message : 'contacts_template_invalid';
        importError.value = root.$t(`unit.${key}`);
      } finally {
        importing.value = false;
      }
    };

    // Add a watch for the entire itemData.value.emergency_contact array
    watch(
      () => itemData.value.emergency_contact,
      () => {
        emit("update:itemData", itemData.value);
      },
      { deep: true }
    );

    const addContact = () => {
      if (!itemData.value.emergency_contact) {
        itemData.value.emergency_contact = [];
      }
      itemData.value.emergency_contact.push({
        section: "",
        name: "",
        phone_number: "",
        description: "",
      });
      emit("update:itemData", itemData.value);
    };

    const removeContact = (index) => {
      itemData.value.emergency_contact.splice(index, 1);
      emit("update:itemData", itemData.value);
    };

    // Validación del paso
    const validateStep = async () => {
      if (importing.value) return false;
      return await refFormObserver.value.validate();
    }

    return {
      ...templateDownload,
      showImport,
      templateFile,
      importing,
      importError,
      importContacts,
      itemData,
      teamsOptions,
      validateStep,
      addContact,
      removeContact,
      refFormObserver,
      getValidationState,
    };
  },
};
</script>
